// Item definitions registry. Per plan §4: balance constants live as data.
// Edit these to retune gear; module logic doesn't change.

import type { ItemKind, EquipSlot } from './index.js';
import type { DamageType } from '../combat/index.js';

export interface StatBlock {
  hpBonus: number;
  damageBonus: number;
  resistances: Partial<Record<DamageType, number>>;
}

export interface ItemDef {
  id: string;
  name: string;
  kind: ItemKind;
  slot: EquipSlot | null;
  tier: number;
  durabilityMax: number;
  weaponId?: string; // present for weapons; matches WEAPON_AUTOS in client/app/boot
  archetype?: 'cloth' | 'leather' | 'plate'; // armor only
  stats: StatBlock;
  iconColor: string; // for procedural UI tile until real icons
  description: string;
}

const NO_STATS: StatBlock = { hpBonus: 0, damageBonus: 0, resistances: {} };

const DEFS: ItemDef[] = [
  // ---- Weapons (T2) ----
  {
    id: 'sword.t2',
    name: 'Iron Broadsword',
    kind: 'weapon',
    slot: 'mainHand',
    tier: 2,
    durabilityMax: 240,
    weaponId: 'sword',
    stats: { hpBonus: 0, damageBonus: 6, resistances: { Physical: 0.05 } },
    iconColor: '#c8c8d0',
    description: 'Sustain melee — close, slash, sweep.',
  },
  {
    id: 'bow.t2',
    name: 'Hunter\'s Longbow',
    kind: 'weapon',
    slot: 'mainHand',
    tier: 2,
    durabilityMax: 200,
    weaponId: 'bow',
    stats: { hpBonus: 0, damageBonus: 4, resistances: {} },
    iconColor: '#a87040',
    description: 'Range and burst — multishot, snare, arrow rain.',
  },
  {
    id: 'fireStaff.t2',
    name: 'Apprentice Fire Staff',
    kind: 'weapon',
    slot: 'mainHand',
    tier: 2,
    durabilityMax: 200,
    weaponId: 'fireStaff',
    stats: { hpBonus: 0, damageBonus: 8, resistances: { Magic: 0.05 } },
    iconColor: '#e85020',
    description: 'AoE magic — fireballs, flames, falling stars.',
  },

  // ---- Plate (Phys-leaning, +HP, -Magic) ----
  {
    id: 'plate.helm.t2',
    name: 'Iron Helm',
    kind: 'armor',
    slot: 'head',
    tier: 2,
    durabilityMax: 200,
    archetype: 'plate',
    stats: { hpBonus: 25, damageBonus: 0, resistances: { Physical: 0.08, Magic: 0.02 } },
    iconColor: '#8a8a90',
    description: 'Heavy. Protects the skull at the cost of magic resistance.',
  },
  {
    id: 'plate.chest.t2',
    name: 'Iron Cuirass',
    kind: 'armor',
    slot: 'chest',
    tier: 2,
    durabilityMax: 240,
    archetype: 'plate',
    stats: { hpBonus: 60, damageBonus: 0, resistances: { Physical: 0.15, Magic: 0.03 } },
    iconColor: '#8a8a90',
    description: 'Solid plate. Big HP pool, slow.',
  },
  {
    id: 'plate.shoes.t2',
    name: 'Iron Sabatons',
    kind: 'armor',
    slot: 'shoes',
    tier: 2,
    durabilityMax: 180,
    archetype: 'plate',
    stats: { hpBonus: 20, damageBonus: 0, resistances: { Physical: 0.06, Magic: 0.01 } },
    iconColor: '#8a8a90',
    description: 'Heavy boots. Sturdy.',
  },

  // ---- Leather (balanced, light bonuses) ----
  {
    id: 'leather.helm.t2',
    name: 'Hunter\'s Hood',
    kind: 'armor',
    slot: 'head',
    tier: 2,
    durabilityMax: 160,
    archetype: 'leather',
    stats: { hpBonus: 12, damageBonus: 1, resistances: { Physical: 0.04, Magic: 0.04 } },
    iconColor: '#8a5a30',
    description: 'Soft hood. Balanced.',
  },
  {
    id: 'leather.chest.t2',
    name: 'Hunter\'s Jerkin',
    kind: 'armor',
    slot: 'chest',
    tier: 2,
    durabilityMax: 200,
    archetype: 'leather',
    stats: { hpBonus: 30, damageBonus: 2, resistances: { Physical: 0.08, Magic: 0.07 } },
    iconColor: '#8a5a30',
    description: 'Tanned hide. Nimble enough to draw a bow.',
  },
  {
    id: 'leather.shoes.t2',
    name: 'Hunter\'s Boots',
    kind: 'armor',
    slot: 'shoes',
    tier: 2,
    durabilityMax: 140,
    archetype: 'leather',
    stats: { hpBonus: 10, damageBonus: 1, resistances: { Physical: 0.03, Magic: 0.03 } },
    iconColor: '#8a5a30',
    description: 'Quiet on the forest floor.',
  },

  // ---- Cloth (Magic-leaning, low Phys) ----
  {
    id: 'cloth.helm.t2',
    name: 'Apprentice Cowl',
    kind: 'armor',
    slot: 'head',
    tier: 2,
    durabilityMax: 140,
    archetype: 'cloth',
    stats: { hpBonus: 6, damageBonus: 3, resistances: { Physical: 0.02, Magic: 0.09 } },
    iconColor: '#5a3a8a',
    description: 'Woven runes. Trades armor for magic focus.',
  },
  {
    id: 'cloth.chest.t2',
    name: 'Apprentice Robe',
    kind: 'armor',
    slot: 'chest',
    tier: 2,
    durabilityMax: 180,
    archetype: 'cloth',
    stats: { hpBonus: 15, damageBonus: 5, resistances: { Physical: 0.04, Magic: 0.16 } },
    iconColor: '#5a3a8a',
    description: 'Long robe. The flames whisper to it.',
  },
  {
    id: 'cloth.shoes.t2',
    name: 'Apprentice Sandals',
    kind: 'armor',
    slot: 'shoes',
    tier: 2,
    durabilityMax: 120,
    archetype: 'cloth',
    stats: { hpBonus: 4, damageBonus: 2, resistances: { Physical: 0.01, Magic: 0.07 } },
    iconColor: '#5a3a8a',
    description: 'Light steps for light spells.',
  },

  // ---- Resources (no stats; for crafting later) ----
  {
    id: 'wolfPelt',
    name: 'Wolf Pelt',
    kind: 'resource',
    slot: null,
    tier: 2,
    durabilityMax: 0,
    stats: NO_STATS,
    iconColor: '#5a4030',
    description: 'A coarse pelt. Tans into leather.',
  },
  {
    id: 'wolfFang',
    name: 'Wolf Fang',
    kind: 'resource',
    slot: null,
    tier: 2,
    durabilityMax: 0,
    stats: NO_STATS,
    iconColor: '#e8e0c8',
    description: 'A sharp fang. Used in T2 weapons.',
  },
  {
    id: 'silverShard',
    name: 'Silver Shard',
    kind: 'resource',
    slot: null,
    tier: 2,
    durabilityMax: 0,
    stats: NO_STATS,
    iconColor: '#d8d8e8',
    description: 'A glint of currency.',
  },

  // ---- Raw materials (gathered from nodes) ----
  ...rawMaterial('rawWood', 'Raw Wood', 1, '#8a5a30', 'Pine logs from a sapling.'),
  ...rawMaterial('rawWood', 'Raw Wood', 2, '#7a4520', 'Oak logs.'),
  ...rawMaterial('rawWood', 'Raw Wood', 3, '#5a3a22', 'Hardwood logs.'),
  ...rawMaterial('rawOre', 'Raw Ore', 1, '#6a6a78', 'Tin ore.'),
  ...rawMaterial('rawOre', 'Raw Ore', 2, '#7a7a8a', 'Iron ore.'),
  ...rawMaterial('rawOre', 'Raw Ore', 3, '#9a9a8a', 'Steel ore.'),
  ...rawMaterial('rawFiber', 'Raw Fiber', 1, '#9aa050', 'Cotton fiber.'),
  ...rawMaterial('rawFiber', 'Raw Fiber', 2, '#7a8848', 'Linen fiber.'),
  ...rawMaterial('rawFiber', 'Raw Fiber', 3, '#5a8030', 'Silk fiber.'),

  // ---- Refined materials (from refining station) ----
  ...refined('planks', 'Planks', 2, '#a87040', 'Cut planks. T2 construction.'),
  ...refined('planks', 'Planks', 3, '#8a5a30', 'Cut planks. T3 construction.'),
  ...refined('ironBar', 'Iron Bar', 2, '#8888a0', 'Smelted bar.'),
  ...refined('ironBar', 'Iron Bar', 3, '#a0a0c0', 'Steel bar.'),
  ...refined('cloth', 'Cloth', 2, '#c8a868', 'Woven cloth.'),
  ...refined('cloth', 'Cloth', 3, '#d8c878', 'Fine cloth.'),
];

function rawMaterial(baseId: string, baseName: string, tier: number, color: string, description: string): ItemDef[] {
  return [
    {
      id: `${baseId}.t${tier}`,
      name: `${baseName} (T${tier})`,
      kind: 'resource',
      slot: null,
      tier,
      durabilityMax: 0,
      stats: NO_STATS,
      iconColor: color,
      description,
    },
  ];
}

function refined(baseId: string, baseName: string, tier: number, color: string, description: string): ItemDef[] {
  return [
    {
      id: `${baseId}.t${tier}`,
      name: `${baseName} (T${tier})`,
      kind: 'refined',
      slot: null,
      tier,
      durabilityMax: 0,
      stats: NO_STATS,
      iconColor: color,
      description,
    },
  ];
}

const DEFS_BY_ID = new Map<string, ItemDef>(DEFS.map((d) => [d.id, d]));

export function defOf(id: string): ItemDef | null {
  return DEFS_BY_ID.get(id) ?? null;
}

export function allDefs(): ReadonlyArray<ItemDef> {
  return DEFS;
}
